import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';

@Component({
  selector: 'app-price-input',
  imports: [CommonModule],
  templateUrl: './price-input.component.html',
  styleUrl: './price-input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PriceInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PriceInputComponent),
      multi: true,
    },
  ],
})
export class PriceInputComponent implements ControlValueAccessor, Validator, OnDestroy {
  @Input() label = 'Precio';
  @Input() inputId = 'priceInput';
  @Input() placeholder = '0';
  @Input() symbol = '$';
  @Input() min: number | null = null;
  @Input() max: number | null = null;
  @Input() debounceMs = 650;
  @Input() disabled = false;
  @Input() error: string | null = null;

  @Output() valueChanged = new EventEmitter<string>();

  displayValue = '';

  private rawValue = '';
  private formatTimeout: ReturnType<typeof setTimeout> | null = null;
  private onChange = (value: string) => {};
  private onTouched = () => {};

  get isDisabled(): boolean {
    return this.disabled;
  }

  ngOnDestroy(): void {
    this.clearFormatTimeout();
  }

  writeValue(value: string | number | null): void {
    this.rawValue = this.normalizeIncomingValue(value);
    this.displayValue = this.formatValue(this.rawValue);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(_: AbstractControl): ValidationErrors | null {
    if (!this.rawValue) return null;

    const numericValue = Number(this.rawValue);

    if (!Number.isFinite(numericValue)) {
      return { price: true };
    }

    if (this.min !== null && numericValue < this.min) {
      return { min: { min: this.min, actual: numericValue } };
    }

    if (this.max !== null && numericValue > this.max) {
      return { max: { max: this.max, actual: numericValue } };
    }

    return null;
  }

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;

    this.displayValue = input.value;
    this.rawValue = this.parseDisplayValue(input.value);

    this.onChange(this.rawValue);
    this.valueChanged.emit(this.rawValue);
    this.scheduleFormat();
  }

  onBlur(): void {
    this.onTouched();
    this.formatNow();
  }

  onFocus(): void {
    this.clearFormatTimeout();
  }

  private scheduleFormat(): void {
    this.clearFormatTimeout();

    this.formatTimeout = setTimeout(() => {
      this.formatNow();
    }, this.debounceMs);
  }

  private formatNow(): void {
    this.clearFormatTimeout();
    this.displayValue = this.formatValue(this.rawValue);
  }

  private clearFormatTimeout(): void {
    if (!this.formatTimeout) return;

    clearTimeout(this.formatTimeout);
    this.formatTimeout = null;
  }

  private normalizeIncomingValue(value: string | number | null): string {
    if (value === null || value === undefined || value === '') return '';

    return this.parseDisplayValue(String(value));
  }

  private parseDisplayValue(value: string): string {
    if (!value) return '';

    const systemValue = value.replace(/\./g, '').replace(/,/g, '.');

    const cleanedValue = systemValue.replace(/[^\d.]/g, '');

    if (!cleanedValue) return '';

    const firstDotIndex = cleanedValue.indexOf('.');

    let integerPart = cleanedValue;
    let decimalPart = '';

    if (firstDotIndex >= 0) {
      integerPart = cleanedValue.slice(0, firstDotIndex);
      decimalPart = cleanedValue.slice(firstDotIndex + 1).replace(/\./g, '');
    }

    const normalizedInteger = integerPart.replace(/^0+(?=\d)/, '');
    const normalizedDecimal = decimalPart.slice(0, 2);

    const safeInteger = normalizedInteger || '0';

    if (!normalizedDecimal && !cleanedValue.endsWith('.')) {
      return safeInteger;
    }
    return `${safeInteger}.${normalizedDecimal}`;
  }


  private formatValue(value: string): string {
    if (!value) return '';

    const [integerPart, decimalPart] = value.split('.');
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

    if (!decimalPart) return formattedInteger;

    return `${formattedInteger},${decimalPart}`;
  }
}
